import { watch } from 'vue';
import { useWindowSize } from '@vueuse/core';
import { useDebounce } from '@nuxt-common/media-card-large/app/composables/useDebounce';
import { useImageZoomerGestures } from '~/modules/image-zoomer/composables/useImageZoomerGestures';
import { useGestureListeners } from '~/modules/image-zoomer/composables/useGestureListeners';
import useFeatureEligibility from '~/modules/featureControl/composables/useFeatureEligibility';
import { useMainImageInteracted } from '~/composables/useMainImageInteracted';
import { useImageryState } from '~/composables/useImageryState';

/**
 *
 * @param {import('vue').Ref<HTMLElement>} imagesContainer
 * @param {Boolean} topLevelApplied
 * @returns {{
 *     start: () => {}
 *     destroy: () => {}
 *     start: () => void
 *     destroy: () => void
 *     resetImage: () => void
 *     isZoomedIn: import('vue').ComputedRef<boolean>
 *   }}
 */
export const useImageZoomer = (imagesContainer, topLevelApplied) => {
  const { stopAutoPlay } = useMainImageInteracted();
  const { zoomStates, isZoomedIn } = useImageryState();
  const { isTopLevelZoomerClick } = useFeatureEligibility();

  const { tapStart, pinchStart, pinch, pinchEnd, mouseMove, pan, panEnd, tap, onEnter, onArrowPressed, resetImage, hoverEnd } =
    useImageZoomerGestures(
      imagesContainer,
      {
        isTopLevelZoomerClick: isTopLevelZoomerClick && topLevelApplied,
      },
      stopAutoPlay,
      zoomStates,
    );
  const { width, height } = useWindowSize();
  const gestureEvents = {
    tapStart,
    pinchStart,
    pinch,
    pinchEnd,
    mouseMove,
    pan,
    panEnd,
    tap,
    onEnter,
    onArrowPressed,
    ...(isTopLevelZoomerClick && topLevelApplied && hoverEnd ? { hoverEnd } : {}),
  };

  const { start: startGestures, stopListening } = useGestureListeners(imagesContainer, gestureEvents, {
    isTopLevelZoomerClick: isTopLevelZoomerClick && topLevelApplied,
  });

  const start = (bodyOnly = false) => {
    startGestures(bodyOnly);
  };

  const destroy = (bodyOnly = false) => {
    if (resetImage) {
      resetImage();
    }
    stopListening(bodyOnly);
  };

  const updateVisibilityDebounced = useDebounce(resetImage, 50);
  if (!topLevelApplied) {
    watch([width, height], updateVisibilityDebounced);
  }

  return {
    start,
    destroy,
    resetImage,
    isZoomedIn,
  };
};

export default useImageZoomer;
